/** biome-ignore-all lint/style/useNamingConvention: camel case */
import moment from 'moment-timezone';
import {useCallback} from 'react';
import {trackEventId} from '~/CreateFabricGreetingCard/utils/dataTracking';
import {
  webViewOnNav,
  webViewSetBusy,
  webViewSetEventId,
} from '~/CreateFabricGreetingCard/webview/webview';
import {useRequestLogin} from '~/common/fabric/Card/components/LoginModal/hooks/useRequestLogin';
import {loginModalPromise} from '~/common/fabric/Card/components/LoginModal/utils/loginModalPromise';
import {getLoggedUserId} from '~/common/fabric/User/reducers/user';
import {isFabricWebview} from '~/common/utils/webview';
import {useSaveEcardMutation} from '~/QuickEcards/api/ecard';
import {history} from '~/QuickEcards/history';
import {AppStore, useAppSelector} from '~/QuickEcards/store';
import {updateEcard} from '~/QuickEcards/store/reducers/ecard';
import {Ecard} from '~/QuickEcards/types/ecard';
import {Step} from '~/QuickEcards/types/misc';
import {handleRetrievedCardData} from '~/QuickEcards/utils/ecard';
import {
  backToGallery,
  getCurrentStep,
  steps,
  updateEventIdInPathname,
} from '~/QuickEcards/utils/navigation';
import {toastAlert} from '~library/atoms/Toast';
import {AlertLineIcon} from '~library/svg/AlertLineIcon';

export type NavigateToRouteFn = (
  routeNameArg: Step | 'next' | 'back' | 'previous' | null,
  store: AppStore,
  force?: boolean,
  searchParams?: Record<string, string>
) => Promise<void | boolean | undefined>;

export const useNavigateToRoute = () => {
  const {requestLogin} = useRequestLogin();
  const [saveEcard] = useSaveEcardMutation();
  const isLoggedIn = useAppSelector(getLoggedUserId);

  const navigateToRoute: NavigateToRouteFn = useCallback(
    async (
      routeNameArg,
      store,
      // biome-ignore lint/style/useDefaultParameterLast: default param should be last
      force = false,
      searchParams
    ) => {
      let routeName = routeNameArg;
      if (!routeName) {
        // No route name specified; return to gallery
        return backToGallery();
      }

      const currentStep = getCurrentStep();
      if (routeName === 'exit' && currentStep?.id === 1) {
        window.history.back();
        return false;
      }

      if (!isLoggedIn) {
        const loggedIn = await requestLogin();
        if (!loggedIn) return false;
      }

      const checkedOut: boolean = store.getState().review?.checkedOut;

      if (routeName === 'next') {
        routeName = currentStep?.nextRoute ?? null;
      }
      if (routeName === 'back') window.history.back();
      if (
        (routeName !== 'previous' || currentStep?.runPreLeaveForPrevious) &&
        !force &&
        currentStep?.preLeaveRouteFunction
      ) {
        try {
          const cont = await currentStep.preLeaveRouteFunction(store);
          if (!cont)
            /* preLeaveRouteFunction returns false to abort navigation */
            return undefined;
        } catch (e: unknown) {
          const error = e as Error;
          if (error?.message) {
            evite.error('Failure in preLeaveRouteFunction for greeting cards', e);
            toastAlert(error.message, {
              icon: <AlertLineIcon />,
              type: 'error',
              position: 'top-right',
            });
          }
          return undefined;
        }
      }
      if (routeName === 'previous') {
        if (typeof currentStep?.previousRoute === 'function') {
          routeName = currentStep?.previousRoute(store);
        } else {
          routeName = currentStep?.previousRoute ?? null;
        }
      }
      if (!force && currentStep?.saveOnLeave && !checkedOut) {
        webViewSetBusy(true);
        try {
          const userId = store.getState().user?.userId;
          if (!userId)
            try {
              await loginModalPromise(store.dispatch);
            } catch (e: unknown) {
              evite.error(e);
              // login window canceled
              return undefined;
            }
          const origEventId = store.getState().ecard.id;

          // Configure request body for saveEcard
          const {ecard, user} = store.getState();
          const body: Ecard = {
            ...ecard,
            event_data: {
              ...ecard.event_data,
              start: ecard.event_data.start || moment.utc().format('YYYY-MM-DDTHH:mm:ss.SSS'),
              event_host_name: ecard.event_data.event_host_name || user.userName,
            },
            sender_data: {
              ...ecard.sender_data,
              host_id: user.userId,
            },
          };

          const {data} = await saveEcard(body);
          if (!data) throw new Error(`Failed to save ${origEventId ? 'changes' : 'draft'}`);
          handleRetrievedCardData(data, store);
          store.dispatch(updateEcard(data));
          const newEventId = store.getState().ecard.id;
          if (newEventId !== origEventId) {
            webViewSetEventId(newEventId);
            trackEventId(newEventId);

            updateEventIdInPathname(newEventId);
          }
        } catch (e: unknown) {
          evite.error(e);
          webViewSetBusy(false);
          return false;
        } finally {
          webViewSetBusy(false);
        }
      }

      const route = routeName && routeName !== 'back' ? steps[routeName] : undefined;
      if (!route) {
        return undefined;
      }
      if (!force && route.preEnterRouteFunction) {
        try {
          webViewSetBusy(true);
          const cont = await route.preEnterRouteFunction(store);
          if (cont === false)
            /* preEnterRouteFunction returns false to abort navigation */
            return undefined;
        } catch (e: unknown) {
          const error = e as Error;
          if (error?.message) {
            evite.error('Failure in preEnterRouteFunction for greeting cards', e);
            toastAlert(error.message, {
              icon: <AlertLineIcon />,
              type: 'error',
              position: 'top-right',
            });
          }
          return undefined;
        } finally {
          webViewSetBusy(false);
        }
      }
      if (isFabricWebview() && route.hideInWebView) {
        // Only notify the app of the new route, don't update our pathname
        webViewOnNav(routeName, route.nextRoute, route.previousRoute, route.nextButtonText);
      } else if (route.path.indexOf('/') < 0) {
        const components = history.location.pathname.split('/');
        components[components.length - 1] = route.path;
        const {search} = history.location;
        const queryParams = new URLSearchParams(search);
        if (searchParams) {
          Object.entries(searchParams as Record<string, string>).forEach((sp) => {
            queryParams.set(sp[0], sp[1]);
          });
        }

        history.push({
          pathname: components.join('/'),
          hash: history.location.hash,
          search: queryParams.toString(),
        });
      } else {
        window.location.href = route.path;
      }
      return undefined;
    },
    [requestLogin, isLoggedIn, saveEcard]
  );

  return navigateToRoute;
};
